import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { AgentSchema } from "./schemas/agent.schema";
import { AgentDetailSchema } from "./schemas/agent-detail.schema";
import { CreateAgentBodySchema } from "./schemas/create-agent-body.schema";
import { DeleteAgentResponseSchema } from "./schemas/delete-agent-response.schema";
import {
  AgentProfilePictureFormDataBodySchema,
  AgentProfilePictureResultSchema,
  RegenerateProfilePictureBodySchema
} from "./schemas/profile-picture.schema";
import { UpdateAgentBodySchema } from "./schemas/update-agent-body.schema";

/**
 * ZAgents - Zod Validation Schemas for Agents API
 */
export const ZAgents = {
  List: {
    method: "GET" as const,
    path: "/api/agents",
    Response: ApiSuccessSchema(z.array(AgentSchema))
  },

  Create: {
    method: "POST" as const,
    path: "/api/agents",
    Body: CreateAgentBodySchema,
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  Get: {
    method: "GET" as const,
    path: "/api/agents/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  Update: {
    method: "PUT" as const,
    path: "/api/agents/:id",
    PathVars: z.object({ id: z.string() }),
    Body: UpdateAgentBodySchema,
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  Delete: {
    method: "DELETE" as const,
    path: "/api/agents/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(DeleteAgentResponseSchema)
  },

  Duplicate: {
    method: "POST" as const,
    path: "/api/agents/:id/duplicate",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  /**
   * GET /api/agents/thread/:threadId
   * Find an agent by the agent-creation-assistant thread that created it.
   * Returns null if the thread exists but the agent has not been created yet.
   * Static route — declared before :id routes in the controller.
   */
  GetByThread: {
    method: "GET" as const,
    path: "/api/agents/thread/:threadId",
    PathVars: z.object({ threadId: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema.nullable())
  },

  /**
   * POST /api/agents/:id/convert-to-tiptap
   * Convert a legacy-format agent prompt to TipTap JSON.
   * One-time migration per agent — throws if promptJson is already set.
   * Returns the full agent detail after conversion.
   */
  ConvertToTiptap: {
    method: "POST" as const,
    path: "/api/agents/:id/convert-to-tiptap",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  /**
   * DELETE /api/agents/:id/code-interpreter-skills
   * Delete the code interpreter skills ZIP from S3 and clear the URL on the agent.
   */
  DeleteCodeInterpreterSkills: {
    method: "DELETE" as const,
    path: "/api/agents/:id/code-interpreter-skills",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.object({ success: z.literal(true) }))
  },

  /**
   * GET /api/agents/:id/code-interpreter-skills/download
   * Stream the skills ZIP file through the backend — no presigned S3 URL exposed.
   * Note: the path ends in /download — declared AFTER /:id/code-interpreter-skills
   * (DELETE) in the controller to avoid routing conflicts.
   */
  GetCodeInterpreterSkillsDownloadUrl: {
    method: "GET" as const,
    path: "/api/agents/:id/code-interpreter-skills/download",
    PathVars: z.object({ id: z.string() }),
    responseType: "blob" as const
  },

  /**
   * PUT /api/agents/:id/stop
   * Cancel an in-progress agent generation.
   * If the agent is in DRAFT status, it is deleted.
   * If already published or not found, this is a no-op (idempotent).
   * No 404 — callers do not need to know the agent's final state.
   */
  Stop: {
    method: "PUT" as const,
    path: "/api/agents/:id/stop",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.object({ stopped: z.boolean() }))
  },

  /**
   * GET /api/agents/prompts/format-rules/:deploymentType
   * Returns format rules prompt text for the given deployment type.
   * Used by the prompt editor's slash-command snippet insertion.
   * Resolves the prompt store, falling back to static rules from the format registry.
   * Static prefix "prompts/" — must be declared before :id routes in the controller.
   */
  GetFormatRules: {
    method: "GET" as const,
    path: "/api/agents/prompts/format-rules/:deploymentType",
    PathVars: z.object({ deploymentType: z.string() }),
    Response: ApiSuccessSchema(z.object({ promptText: z.string() }))
  },

  /**
   * POST /api/agents/:id/pause
   * Pause a published agent — blocks all AI execution for this agent.
   * Only PUBLISHED agents can be paused. Returns the updated agent detail.
   */
  Pause: {
    method: "POST" as const,
    path: "/api/agents/:id/pause",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  /**
   * POST /api/agents/:id/resume
   * Resume a paused agent — restores it to PUBLISHED status.
   * Only PAUSED agents can be resumed. Returns the updated agent detail.
   */
  Resume: {
    method: "POST" as const,
    path: "/api/agents/:id/resume",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AgentDetailSchema)
  },

  /**
   * POST /api/agents/:id/profile-picture
   * Multipart upload — body is FormData (multer parses the file server-side).
   */
  UploadProfilePicture: {
    method: "POST" as const,
    path: "/api/agents/:id/profile-picture",
    PathVars: z.object({ id: z.string() }),
    Body: AgentProfilePictureFormDataBodySchema,
    Response: ApiSuccessSchema(AgentProfilePictureResultSchema)
  },

  /** POST /api/agents/:id/regenerate-profile-picture — AI-generated portrait */
  RegenerateProfilePicture: {
    method: "POST" as const,
    path: "/api/agents/:id/regenerate-profile-picture",
    PathVars: z.object({ id: z.string() }),
    Body: RegenerateProfilePictureBodySchema,
    Response: ApiSuccessSchema(AgentProfilePictureResultSchema)
  }
} as const;
